<template>
  <div v-if="text" class="additional-item-inline">
    <component v-bind:is="iconComponent" v-if="iconComponent" class="icon" />
    <span ref="truncTarget" class="text" v-ifield-b-tooltip="tooltipOption">
      {{ text }}
    </span>
    <!-- ステイタスの色（仕様: cluster.png） -->
    <div
      v-if="dotColor"
      class="color-dot"
      v-bind:style="{ '--color': dotColor, '--border-color': dotBorderColor }"
    />
  </div>
</template>

<script>
import IconBattery from '@/assets/ifield/ic_battery.svg?component'
import IconBuilding from '@/assets/ifield/ic_building.svg?component'
import IconCheckin from '@/assets/ifield/ic_checkin.svg?component'
import IconOperatingStatus from '@/assets/ifield/ic_operating-status.svg?component'
import IconPlace from '@/assets/ifield/ic_place.svg?component'
import IconPositioningAccuracy from '@/assets/ifield/ic_positioning-accuracy.svg?component'
import IconPositioningTime from '@/assets/ifield/ic_positioning-time.svg?component'
import IconSensor from '@/assets/ifield/ic_sensor.svg?component'
import IconStatus from '@/assets/ifield/ic_status.svg?component'
import { useIsTruncated } from '@/composables/UseIsTruncated'
import { getDotBorderColor } from '@/js/util/UserLabelUtil.js'

/**
 * アイコン名と部品の対応
 * 名前は `js/util/UserLabelItemText.js` の `ITEM_ICON` と合わせる
 */
const ICON_COMPONENT = {
  'battery': IconBattery,
  'building': IconBuilding,
  'checkin': IconCheckin,
  'operating-status': IconOperatingStatus,
  'place': IconPlace,
  'positioning-accuracy': IconPositioningAccuracy,
  'positioning-time': IconPositioningTime,
  'sensor': IconSensor,
  'status': IconStatus,
}

/**
 * 表示項目の値を1行で出す部品
 *
 * ユーザークラスター時のリストの2段目に使う。
 * 値はユーザーリストと同じ文字数で省略し、**省略したときだけマウスホバーで全文を出す**
 */
export default {
  name: 'AppUserAdditionalItemInline',
  props: {
    /**
     * 表示する文字列
     */
    text: {
      type: String,
      default: '',
    },
    /**
     * アイコン名(`UserLabelItemText.js` の `getItemIcon` の戻り)
     */
    icon: {
      type: String,
      default: null,
    },
    /**
     * 値の後ろに出す色（ステイタスの色）
     */
    dotColor: {
      type: String,
      default: null,
    },
  },
  setup() {
    const { truncTarget, isTruncated } = useIsTruncated()
    return { truncTarget, isTruncated }
  },
  computed: {
    /**
     * 色の丸の枠線の色
     * 背景（白）に近い色は丸が見えなくなるため、暗くした色で枠線を付ける
     * @type {String|null}
     */
    dotBorderColor: function () {
      return this.dotColor ? getDotBorderColor(this.dotColor, '#ffffff') : null
    },
    /**
     * アイコンの部品
     * @type {Object|null}
     */
    iconComponent: function () {
      return ICON_COMPONENT[this.icon] || null
    },
    /**
     * ツールチップの設定
     * @type {Object}
     */
    tooltipOption: function () {
      return {
        title: this.text,
        disabled: !this.isTruncated,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.additional-item-inline {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-size: $font-size-small;
  color: $text-color-weak;

  .icon {
    flex: none;
    width: 12px;
    height: 12px;
    fill: $text-color-weak;
  }

  .text {
    min-width: 0;
    overflow-x: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .color-dot {
    flex: none;
    width: 0.625rem;
    height: 0.625rem;
    background-color: var(--color);
    border: 1px solid var(--border-color);
    border-radius: 50%;
  }
}
</style>
